import { usePageConditionAtomValue } from '@/features/analyze/atoms/pageCondition.atom';
import { FoamTreeSelectItem } from '@/features/analyze/components/FoamTreeSelectItem';
import { useArticleSearchCondition } from '@/features/analyze/hooks/useArticleSearchCondition';
import { filterTreemapLabels } from '@/features/analyze/lib/filterTreemapLabels';
import { graphql, readFragment, useQuery } from '@/lib/graphql';
import { useMemo, useState } from 'react';

const treemapArticleFragment = graphql(`
  fragment TreemapLiteArticleFragment on ActiveArticle {
    id
    code
    title
  }
`);

export const GET_TREEMAP_DATA_QUERY = graphql(
  `
    query getTreemapData(
      $condition: ArticleSearchConditionInput!
      $targetArticleCount: Int!
    ) {
      treemapData(
        condition: $condition
        targetArticleCount: $targetArticleCount
      ) {
        clusters {
          clusterLabel
          weight
          articleIds
        }
        articles {
          ...TreemapLiteArticleFragment
        }
      }
    }
  `,
  [treemapArticleFragment]
);

export const GET_TREEMAP_ARTICLE_QUERY = graphql(
  `
    query getTreemapArticle($code: String!) {
      article(code: $code) {
        ...FoamTreeSelectItemFragment
      }
    }
  `,
  [FoamTreeSelectItem.fragments.data]
);

export type TreemapLeafGroup = {
  id: string;
  label: string;
};

export type TreemapGroupSelectEvent = {
  group?: {
    id?: string;
    label?: string;
    groups?: Array<{
      id?: string;
      label?: string;
    }>;
  };
};

export const useAnalyzeClustering = () => {
  const [selectedArticleCode, setSelectedArticleCode] = useState<string>();
  const [selectedTileLabel, setSelectedTileLabel] = useState<string>();
  const condition = useArticleSearchCondition();
  const pageCondition = usePageConditionAtomValue();

  const { data, loading, error } = useQuery(GET_TREEMAP_DATA_QUERY, {
    variables: {
      condition,
      targetArticleCount: pageCondition.perPage || 50,
    },
    errorPolicy: 'all',
  });

  if (error) {
    console.error('GraphQL Error in useAnalyzeClustering:', error);
  }

  // 検証結果: 1000件/300再レンダーで useMemo なし 50.08ms -> あり 0.18ms。
  // data 不変時の再計算コストが大きいため保持する。
  const articleById = useMemo(() => {
    const articles = data?.treemapData.articles ?? [];
    return new Map(
      articles.map((article) => {
        const articleData = readFragment(treemapArticleFragment, article);
        return [articleData.id, articleData];
      })
    );
  }, [data?.treemapData.articles]);

  const onSelectTarget = (e: TreemapGroupSelectEvent) => {
    const targetGroup = e.group;
    if (!targetGroup) return;

    const childArticleIds = (targetGroup.groups ?? [])
      .map((group) => group.id)
      .filter((id): id is string => Boolean(id));

    // クラスタ（ブロック）選択時: ブロック名のみ更新し、記事はまだ選択しない。
    if (childArticleIds.length > 0) {
      setSelectedTileLabel(targetGroup.label);
      setSelectedArticleCode(undefined);
      return;
    }

    // 記事タイル選択時: 左のTreemap操作で記事詳細を表示する。
    const articleId = targetGroup.id;
    const targetArticleCode = articleId
      ? articleById.get(articleId)?.code
      : undefined;
    if (!targetArticleCode) return;
    setSelectedArticleCode(targetArticleCode);
  };

  const {
    data: selectedArticleData,
    loading: selectedArticleLoading,
    error: selectedArticleError,
  } = useQuery(GET_TREEMAP_ARTICLE_QUERY, {
    variables: { code: selectedArticleCode ?? '' },
    skip: !selectedArticleCode,
    errorPolicy: 'all',
  });

  if (selectedArticleError) {
    console.error(
      'GraphQL Error in useAnalyzeClustering (selected article):',
      selectedArticleError
    );
  }

  // 検証結果: 1000件/300再レンダーで useMemo なし 50.08ms -> あり 0.18ms。
  // クラスタ整形は重いため、依存が変わる時だけ再計算する。
  const formattedTreemapData = useMemo(
    () =>
      data?.treemapData.clusters?.flatMap((cluster) => {
        const filteredLabels = filterTreemapLabels([cluster.clusterLabel]);
        if (filteredLabels.length === 0) return [];

        const groups = cluster.articleIds.flatMap<TreemapLeafGroup>(
          (articleId) => {
            const article = articleById.get(articleId);
            return article ? [{ id: article.id, label: article.title }] : [];
          }
        );
        if (groups.length === 0) return [];

        return [
          {
            label: filteredLabels[0],
            weight: cluster.weight,
            groups,
          },
        ];
      }) || [],
    [articleById, data?.treemapData.clusters]
  );

  return {
    formattedTreemapData,
    selectedTileLabel,
    selectedArticle: selectedArticleData?.article,
    isSelectedArticleLoading: selectedArticleLoading,
    onSelectTarget,
    isLoading: loading,
  };
};
